import axios from 'axios';
import { supabase } from './SupabaseService';
import { PRIMARY_API_URL } from '../config/api';

class FleetService {
  constructor() {
    this.BASE_URL = PRIMARY_API_URL;
  }

  // Helper method to get authorization headers
  getAuthHeaders() {
    const token = localStorage.getItem('admin_token');
    return {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    };
  }

  // VANS

  /**
   * Get all vans from the Supabase database
   * @returns {Promise<Array>} Array of van objects
   */
  async getAllVans() {
    try {
      console.log("Getting all vans from Supabase");
      
      // First try to get vans from Supabase
      let { data: vans, error } = await supabase
        .from('vans')
        .select('*')
        .order('id');
        
      if (error) {
        console.error("Error fetching vans from Supabase:", error);
        throw new Error(`Supabase error: ${error.message}`);
      }
      
      if (!vans || vans.length === 0) {
        console.log("No vans found in Supabase, falling back to direct API call");
        // Fall back to the API if no vans returned
        return this.getAllVansDirectAPI();
      }
      
      return vans;
    } catch (error) {
      console.error("Error in getAllVans:", error);
      // Fall back to direct API call if there's an error with Supabase
      return this.getAllVansDirectAPI();
    }
  }
  
  /**
   * Direct API call to get all vans if Supabase fails
   * @private
   * @returns {Promise<Array>} Array of van objects
   */
  async getAllVansDirectAPI() {
    try {
      console.log("Getting all vans through direct API call");
      const timestamp = Date.now(); // Cache busting
      const response = await axios.get(`${this.BASE_URL}/api/fleet/vans?t=${timestamp}`, this.getAuthHeaders());
      
      if (response.data && Array.isArray(response.data)) {
        return response.data;
      } else {
        console.error("Invalid response format from API:", response.data);
        return [];
      }
    } catch (error) {
      console.error("Error fetching vans from API:", error);
      return [];
    }
  }

  /**
   * Find available vans for a given time range and capacity
   * @param {string} startTime - ISO string for booking start time
   * @param {string} endTime - ISO string for booking end time
   * @param {string} returnTime - ISO string for return time (ignored for now)
   * @param {number} requiredCapacity - Required passenger capacity
   * @returns {Promise<Array>} Array of available van objects
   */
  async findAvailableVans(startTime, endTime, returnTime = null, requiredCapacity = 1) {
    try {
      console.log(`Finding available vans for ${requiredCapacity} passengers from ${startTime} to ${endTime}`);
      
      // Calculate how many vans we need
      const vansNeeded = Math.ceil(requiredCapacity / 7);
      console.log(`Need ${vansNeeded} vans for ${requiredCapacity} passengers`);
      
      // Get all vans to check total fleet size
      const allVans = await this.getAllVans();
      
      if (!allVans || allVans.length === 0) {
        console.log("No vans in fleet");
        return [];
      }
      
      console.log(`Total vans in fleet: ${allVans.length}`);
      
      // Check if we have enough vans in the fleet for this booking
      if (vansNeeded > allVans.length) {
        const maxCapacity = allVans.length * 7;
        console.error(`Not enough vans in fleet. Need ${vansNeeded}, but only ${allVans.length} exist.`);
        
        // Throw an error with a user-friendly message
        const error = new Error(`We don't have enough vans in our fleet to accommodate your group of ${requiredCapacity} passengers. Our maximum capacity is ${maxCapacity} passengers.`);
        error.user_message = error.message;
        throw error;
      }
      
      // For now, we'll return all vans since we don't have the backend endpoint working
      // In reality, we should check for conflicts with existing bookings
      // But since the booking creation process will do the final availability check,
      // this is mainly for UI feedback
      
      console.log(`Fleet has enough capacity. Returning ${allVans.length} available vans.`);
      return allVans;
      
    } catch (error) {
      console.error("Error finding available vans:", error);
      
      // If it's our custom error with user message, re-throw it
      if (error.user_message) {
        throw error;
      }
      
      // For other errors, return empty array
      return [];
    }
  }

  /**
   * Check if a specific van is available for a given time range
   * @param {number} vanId - ID of the van to check
   * @param {string} startTime - ISO string for start time
   * @param {string} endTime - ISO string for end time
   * @returns {Promise<boolean>} True if the van is available
   */
  async isVanAvailable(vanId, startTime, endTime) {
    try {
      console.log(`Checking if van ${vanId} is available from ${startTime} to ${endTime}`);
      
      // Get all assignments that might overlap with this time range
      const { data: overlappingAssignments, error } = await supabase
        .from('booking_van_assignments')
        .select('*')
        .eq('van_id', vanId)
        .or(
          `and(start_time.lte.${endTime},end_time.gte.${startTime}),` +
          `and(start_time.gte.${startTime},start_time.lte.${endTime}),` +
          `and(end_time.gte.${startTime},end_time.lte.${endTime})`
        );
      
      if (error) {
        console.error("Error checking van availability:", error);
        return false;
      }
      
      // Also check for manual van blocks
      const { data: vanBlocks, blockError } = await supabase
        .from('van_availability_blocks')
        .select('*')
        .eq('van_id', vanId)
        .or(
          `and(start_time.lte.${endTime},end_time.gte.${startTime}),` +
          `and(start_time.gte.${startTime},start_time.lte.${endTime}),` +
          `and(end_time.gte.${startTime},end_time.lte.${endTime})`
        );
        
      if (blockError) {
        console.error("Error checking van blocks:", blockError);
        return false;
      }
        
      // If there are any overlapping assignments or blocks, the van is not available
      return (!overlappingAssignments || overlappingAssignments.length === 0) &&
             (!vanBlocks || vanBlocks.length === 0);
    } catch (error) {
      console.error("Error in isVanAvailable:", error);
      return false;
    }
  }

  /**
   * Assign a van to a booking (manual, no driver)
   * @param {number} bookingId - ID of the booking
   * @param {number} vanId - ID of the van to assign
   * @param {string} startTime - ISO string for the assignment start time
   * @param {string} endTime - ISO string for the assignment end time
   * @returns {Promise<Object>} The created assignment object
   */
  async assignVan(bookingId, vanId, startTime, endTime) {
    try {
      console.log(`Assigning van ${vanId} to booking ${bookingId} from ${startTime} to ${endTime}`);
      const response = await axios.post(
        `${this.BASE_URL}/api/bookings/${bookingId}/assignments`,
        {
          van_id: vanId,
          start_time: startTime,
          end_time: endTime
        },
        this.getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      console.error("Error assigning van:", error);
      if (error.response && error.response.data && error.response.data.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  }

  /**
   * Unassign all vans from a booking
   * @param {number} bookingId - ID of the booking 
   * @returns {Promise<Object>} The API response
   */
  async unassignAllVans(bookingId) {
    try {
      console.log(`Unassigning all vans from booking ${bookingId}`);
      const response = await axios.delete(
        `${this.BASE_URL}/api/bookings/${bookingId}/assignments`,
        this.getAuthHeaders()
      );
      
      return response.data;
    } catch (error) {
      console.error("Error unassigning vans:", error);
      throw error;
    }
  }

  /**
   * Get all assignments for a booking
   * @param {number} bookingId - ID of the booking
   * @returns {Promise<Array>} Array of assignment objects
   */
  async getBookingAssignments(bookingId) {
    try {
      console.log(`Getting assignments for booking ${bookingId}`);
      const timestamp = Date.now(); // Cache busting
      const response = await axios.get(
        `${this.BASE_URL}/api/bookings/${bookingId}/assignments?t=${timestamp}`,
        this.getAuthHeaders()
      );
      
      return response.data;
    } catch (error) {
      console.error(`Error getting assignments for booking ${bookingId}:`, error);
      return [];
    }
  }

  /**
   * Create a van availability block
   * @param {number} vanId - ID of the van
   * @param {string} startTime - ISO string for block start time
   * @param {string} endTime - ISO string for block end time
   * @param {string} reason - Reason for the block
   * @returns {Promise<Object>} The created block object
   */
  async createVanBlock(vanId, startTime, endTime, reason) {
    try {
      console.log(`Creating van block for van ${vanId} from ${startTime} to ${endTime}`);
      const response = await axios.post(
        `${this.BASE_URL}/api/fleet/van-blocks`,
        {
          van_id: vanId,
          start_time: startTime,
          end_time: endTime,
          reason: reason
        },
        this.getAuthHeaders()
      );
      
      return response.data;
    } catch (error) {
      console.error("Error creating van block:", error);
      if (error.response && error.response.data && error.response.data.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  }

  /**
   * Delete a van availability block
   * @param {number} blockId - ID of the block to delete
   * @returns {Promise<Object>} The API response
   */
  async deleteVanBlock(blockId) {
    try {
      console.log(`Deleting van block ${blockId}`);
      const response = await axios.delete(
        `${this.BASE_URL}/api/fleet/van-blocks/${blockId}`,
        this.getAuthHeaders()
      );
      
      return response.data;
    } catch (error) {
      console.error("Error deleting van block:", error);
      throw error;
    }
  }

  /**
   * Get all van availability blocks
   * @returns {Promise<Array>} Array of block objects
   */
  async getAllVanBlocks() {
    try {
      console.log("Getting all van blocks");
      const timestamp = Date.now(); // Cache busting
      const response = await axios.get(
        `${this.BASE_URL}/api/fleet/van-blocks?t=${timestamp}`,
        this.getAuthHeaders()
      );
      
      if (response.data && response.data.blocks) {
        return response.data.blocks;
      } else {
        return [];
      }
    } catch (error) {
      console.error("Error getting van blocks:", error);
      return [];
    }
  }
}

export default new FleetService(); 